import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addWeeks, eachDayOfInterval, endOfWeek, format, isSameWeek, parseISO, startOfWeek } from 'date-fns';

import { api } from '../api';
import type { Preferences } from '../types';
import MutationErrorToast from '../components/MutationErrorToast';
import PlanWeek from '../plan/PlanWeek';
import RhythmRail from '../plan/RhythmRail';
import BlockModal from '../plan/BlockModal';
import RhythmEditor from '../plan/RhythmEditor';
import RhythmsModal from '../plan/RhythmsModal';
import LogModal from '../plan/LogModal';
import { EventModal, SlotModal, UnplacedModal } from '../plan/SmallModals';
import { usePlan, usePlanActions, useRhythms } from '../plan/data';
import type { Plan as PlanData, PlanBlock, PlanEvent } from '../plan/types';

type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

type Open =
  | { kind: 'block'; block: PlanBlock; day: string }
  | { kind: 'rhythm'; id: string }
  | { kind: 'rhythms'; tab: 'rhythms' | 'sleep' }
  | { kind: 'log' }
  | { kind: 'event'; event: PlanEvent }
  | { kind: 'unplaced'; day: string }
  | { kind: 'slot'; start: Date };

export default function PlanPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const anchor = params.get('date') ? parseISO(params.get('date')!) : new Date();
  const [open, setOpen] = useState<Open | null>(null);

  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.get<Preferences>('/api/preferences')
  });
  const weekStartsOn = (prefsQ.data?.week_starts_on ?? 0) as WeekStartsOn;
  const from = startOfWeek(anchor, { weekStartsOn });
  const to = endOfWeek(anchor, { weekStartsOn });
  const days = useMemo(() => eachDayOfInterval({ start: from, end: to }), [from.getTime(), to.getTime()]);

  const planQ = usePlan(from, to);
  const rhythmsQ = useRhythms();
  const rhythms = rhythmsQ.data ?? [];
  const byId = useMemo(() => new Map(rhythms.map((r) => [r.id, r])), [rhythms]);
  const actions = usePlanActions();
  const plan = planQ.data;

  function goWeek(delta: number) {
    const next = new URLSearchParams(params);
    if (delta === 0) next.delete('date');
    else next.set('date', format(addWeeks(anchor, delta), 'yyyy-MM-dd'));
    setParams(next);
  }

  function moveBlock(block: PlanBlock, start: Date, end: Date) {
    const key = ['plan', format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd')];
    const day = format(start, 'yyyy-MM-dd');
    // Show the block where it was dropped while the server replans.
    qc.setQueryData<PlanData>(key, (old) => {
      if (!old) return old;
      const moved: PlanBlock = { ...block, type: 'pin', start: start.toISOString(), end: end.toISOString() };
      return {
        ...old,
        days: old.days.map((d) => ({
          ...d,
          blocks: [...d.blocks.filter((b) => b.id !== block.id), ...(d.date === day ? [moved] : [])]
        }))
      };
    });
    if (block.type === 'proposal') {
      actions.pin.mutate({ rhythm_id: block.rhythm_id, start: start.toISOString(), end: end.toISOString() });
    } else {
      actions.movePin.mutate({ id: block.id, start: start.toISOString(), end: end.toISOString() });
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || open) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const run = (f: () => void) => {
        e.preventDefault();
        f();
      };
      if (k === 'ArrowRight' || k === 'j') run(() => goWeek(1));
      else if (k === 'ArrowLeft' || k === 'k') run(() => goWeek(-1));
      else if (k === 't') run(() => goWeek(0));
      else if (k === 'l') run(() => setOpen({ kind: 'log' }));
      else if (k === 'r') run(() => setOpen({ kind: 'rhythms', tab: 'rhythms' }));
      else if (k === 'c' || k === 'p') run(() => nav('/'));
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const thisWeek = isSameWeek(anchor, new Date(), { weekStartsOn });
  const heading = `${format(from, 'MMM d')} – ${format(to, from.getMonth() === to.getMonth() ? 'd' : 'MMM d')}`;
  const close = () => setOpen(null);
  const openRhythm = (id: string) => setOpen({ kind: 'rhythm', id });

  return (
    <div className="flex h-dvh w-dvw overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-[52px] flex-shrink-0 items-center justify-between gap-3 border-b border-[var(--border)] px-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => nav('/')}
              className="rounded-md px-2 py-[5px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
              title="Back to the calendar (c)"
            >
              ‹ Calendar
            </button>
            <h1 className="m-0 text-base font-semibold">Plan</h1>
            <span className="text-[0.9rem] text-[var(--text-muted)]">{heading}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen({ kind: 'log' })}
              title="Log what you did (l)"
              className="rounded-md bg-[var(--accent)] px-3 py-[6px] text-[0.8rem] font-medium text-[var(--accent-fg)] hover:opacity-90"
            >
              Log
            </button>
            <button
              type="button"
              onClick={() => setOpen({ kind: 'rhythms', tab: 'rhythms' })}
              title="Rhythms (r)"
              className="rounded-md px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
            >
              Rhythms
            </button>
            <button
              type="button"
              onClick={() => goWeek(0)}
              disabled={thisWeek}
              className="rounded-md px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)] disabled:opacity-40"
            >
              This week
            </button>
            <div className="inline-flex rounded-md border border-[var(--border)]">
              <button aria-label="previous week" onClick={() => goWeek(-1)} className="h-7 w-7 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]">
                ‹
              </button>
              <button aria-label="next week" onClick={() => goWeek(1)} className="h-7 w-7 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]">
                ›
              </button>
            </div>
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          {plan ? (
            <PlanWeek
              days={days}
              plan={plan}
              rhythms={byId}
              onOpenBlock={(block, day) => setOpen({ kind: 'block', block, day })}
              onOpenEvent={(event) => setOpen({ kind: 'event', event })}
              onOpenUnplaced={(day) => setOpen({ kind: 'unplaced', day })}
              onMoveBlock={moveBlock}
              onPickSlot={(start) => setOpen({ kind: 'slot', start })}
            />
          ) : (
            <div className="grid flex-1 place-items-center text-[var(--text-faint)]">{planQ.error ? planQ.error.message : '…'}</div>
          )}
          {plan && (
            <RhythmRail
              plan={plan}
              rhythms={rhythms}
              onOpenRhythm={openRhythm}
              onOpenBlock={(block, day) => setOpen({ kind: 'block', block, day })}
              onOpenSettings={() => setOpen({ kind: 'rhythms', tab: 'sleep' })}
            />
          )}
        </div>
      </main>

      {plan && open?.kind === 'block' && byId.get(open.block.rhythm_id) && (
        <BlockModal
          key={open.block.id}
          block={open.block}
          day={open.day}
          rhythm={byId.get(open.block.rhythm_id)!}
          plan={plan}
          onClose={close}
          onOpenRhythm={() => openRhythm(open.block.rhythm_id)}
        />
      )}
      {plan && open?.kind === 'rhythm' && byId.get(open.id) && (
        <RhythmEditor
          rhythm={byId.get(open.id)}
          status={plan.rhythms.find((s) => s.rhythm_id === open.id)}
          onClose={close}
        />
      )}
      {plan && open?.kind === 'rhythms' && (
        <RhythmsModal settings={plan.settings} statuses={plan.rhythms} initialTab={open.tab} onClose={close} />
      )}
      {plan && open?.kind === 'log' && <LogModal plan={plan} rhythms={rhythms} onClose={close} />}
      {open?.kind === 'event' && (
        <EventModal
          event={open.event}
          rhythm={open.event.rhythm_id ? byId.get(open.event.rhythm_id) : undefined}
          onClose={close}
          onOpenRhythm={openRhythm}
        />
      )}
      {plan && open?.kind === 'unplaced' && (
        <UnplacedModal day={open.day} plan={plan} rhythms={byId} onClose={close} onOpenRhythm={openRhythm} />
      )}
      {open?.kind === 'slot' && <SlotModal start={open.start} rhythms={rhythms} onClose={close} />}
      <MutationErrorToast />
    </div>
  );
}
